import { Component, signal, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { DynamicContentAdminService } from '../../../pages/ContentManager/dynamic-content-admin.service';

export interface MenuItem {
  key: string;
  label: string;
  icon: string;
  route: string;
  queryParams?: Record<string, string>;
  children?: MenuItem[];
  isGroup?: boolean;
}

@Component({
  selector: 'app-sidebar',
  standalone: false,
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, OnDestroy {
  // Tablet (768-1023px): start on the icon rail so the 256px menu does not eat half the screen.
  isOpen = signal(typeof window === 'undefined' || window.innerWidth < 768 || window.innerWidth >= 1024);
  mobileOpen = signal(false);
  currentRoute = signal('');
  expandedItems = signal<Set<string>>(new Set());
  private destroy$ = new Subject<void>();

  menuItems = signal<MenuItem[]>([
    {
      key: 'communication-main',
      label: 'Communication',
      icon: 'campaign',
      route: '',
      children: [
        { key: 'actualites', label: 'Actualités', icon: 'newspaper', route: '/federation/actualites' },
        { key: 'communiques', label: 'Communiqués', icon: 'edit_document', route: '/federation/communiques' },
        { key: 'revues-presse', label: 'Revues de presse', icon: 'article', route: '/federation/revues-presse' },
        { key: 'mediatique', label: 'Médiathèque', icon: 'photo_library', route: '/federation/mediatique' },
        { key: 'reunions', label: 'Réunions', icon: 'groups', route: '/federation/reunions' },
        { key: 'reseaux-sociaux', label: 'Réseaux sociaux', icon: 'share', route: '/federation/reseaux-sociaux' },
      ],
    },
    {
      key: 'gestion-main',
      label: 'Gestion',
      icon: 'manage_accounts',
      route: '',
      children: [
        { key: 'formulaires', label: 'Formulaires', icon: 'description', route: '/federation/formulaires' },
        { key: 'partenaires', label: 'Partenaires', icon: 'handshake', route: '/federation/partenaires' },
        { key: 'documents-publics', label: 'Documents publics', icon: 'folder_open', route: '/federation/documents-publics' },
        { key: 'history-events', label: 'Événements historiques', icon: 'history_edu', route: '/federation/evenements-historiques' },
        { key: 'int-instances', label: 'Instances internationales', icon: 'public', route: '/federation/instances-internationales' },
        { key: 'members', label: 'Membres', icon: 'group', route: '/federation/members' },
        { key: 'bureau-executif', label: 'Bureau Exécutif', icon: 'groups', route: '/federation/bureau-executif' },
        { key: 'administration', label: 'Administration', icon: 'account_tree', route: '/federation/administration' },
      ],
    },
    {
      key: 'content-pages',
      label: 'Contenu des pages',
      icon: 'edit_note',
      route: '/federation/content-manager',
      children: [],
    },
  ]);

  constructor(private router: Router, private dynamicContentAdmin: DynamicContentAdminService) {}

  ngOnInit(): void {
    this.currentRoute.set(this.router.url);
    this.expandedItems.update(s => { const n = new Set(s); n.add('communication-main'); n.add('gestion-main'); n.add('content-pages'); return n; });

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe((event: any) => {
        this.currentRoute.set(event.urlAfterRedirects);
        if (event.urlAfterRedirects.includes('/federation/content-manager')) {
          const contentItem = this.menuItems().find(i => i.key === 'content-pages');
          if (contentItem?.children) {
            this.autoExpandActiveGroup(contentItem.children);
          }
        }
      });

    this.loadPagesIntoSidebar();
  }

  private loadPagesIntoSidebar(): void {
    this.dynamicContentAdmin.getPagesTree()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (!response.success || !response.data) return;

          const children: MenuItem[] = [];
          for (const group of response.data) {
            if (group.children.length > 0) {
              children.push({
                key: `group-${group.page_code || group.page_name}`,
                label: group.page_name,
                icon: 'folder',
                route: '',
                isGroup: true,
                children: group.children.map(child => ({
                  key: `page-${child.page_code}`,
                  label: child.page_name,
                  icon: 'article',
                  route: '/federation/content-manager',
                  queryParams: { page: child.page_code },
                })),
              });
            } else if (group.page_code) {
              children.push({
                key: `page-${group.page_code}`,
                label: group.page_name,
                icon: 'article',
                route: '/federation/content-manager',
                queryParams: { page: group.page_code },
              });
            }
          }

          this.menuItems.update(items =>
            items.map(item =>
              item.key === 'content-pages' ? { ...item, children } : item
            )
          );

          if (this.currentRoute().includes('/federation/content-manager')) {
            this.autoExpandActiveGroup(children);
          }
        }
      });
  }

  private autoExpandActiveGroup(children: MenuItem[]): void {
    this.expandedItems.update(s => {
      const n = new Set(s);
      n.add('content-pages');
      // Expand the group containing the active page
      for (const child of children) {
        if (child.isGroup && child.children?.some(p => this.isChildActive(p))) {
          n.add(child.key);
        }
      }
      return n;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleSidebar(): void {
    this.isOpen.update(state => !state);
  }

  toggleMobile(): void {
    this.mobileOpen.update(v => !v);
    // The phone drawer always shows labels, never the icon rail.
    if (this.mobileOpen() && !this.isOpen()) this.isOpen.set(true);
  }

  closeMobile(): void {
    this.mobileOpen.set(false);
  }

  navigateTo(item: MenuItem): void {
    if (item.isGroup) return;
    if (!item.route) return;
    this.closeMobile();
    this.router.navigate([item.route], { queryParams: item.queryParams ?? {} });
  }

  navigateToParent(item: MenuItem): void {
    if (!item.route) return;
    this.closeMobile();
    this.router.navigate([item.route]);
  }

  toggleDropdown(key: string): void {
    this.expandedItems.update(s => {
      const n = new Set(s);
      if (n.has(key)) { n.delete(key); } else { n.add(key); }
      return n;
    });
  }

  isActive(route: string): boolean {
    if (!route) return false;
    const current = this.currentRoute();
    return current === route || current.startsWith(route + '/') || current.startsWith(route + '?');
  }

  isChildActive(item: MenuItem): boolean {
    if (item.isGroup) return false;
    const current = this.currentRoute();
    const [path, search] = current.split('?');
    const pathMatches = path === item.route || path.startsWith(item.route + '/');
    if (!pathMatches) return false;
    if (!item.queryParams) return true;
    const params = new URLSearchParams(search || '');
    return Object.entries(item.queryParams).every(([k, v]) => params.get(k) === v);
  }

  isParentActive(item: MenuItem): boolean {
    if (!item.children) return false;
    return item.children.some(child => {
      if (child.isGroup) return child.children?.some(p => this.isChildActive(p)) ?? false;
      return this.isChildActive(child);
    });
  }

  isItemExpanded(item: MenuItem): boolean {
    return this.expandedItems().has(item.key) || this.isParentActive(item);
  }

  toggleGroup(key: string): void {
    this.expandedItems.update(s => {
      const n = new Set(s);
      if (n.has(key)) { n.delete(key); } else { n.add(key); }
      return n;
    });
  }

  isGroupExpanded(key: string): boolean {
    return this.expandedItems().has(key);
  }
}
